import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, X, Trash2, MoreHorizontal, AlertTriangle, ShieldCheck, RefreshCw, ShieldAlert, Wand2, Timer, BellRing } from "lucide-react";
import { formatCountdown, formatDateTime, REGISTRARS, STATUS_STYLES } from "@/lib/format";
import { useTimezone } from "@/lib/tz";
import { cancelSnipe, deleteSnipe, fireSnipe, retrySnipe, preflightSnipe, autofixPreflight, api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";

function CountdownCell({ target, status, domain }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (status !== "scheduled") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [status]);
  if (status !== "scheduled")
    return (
      <span className="text-[#A1A1AA] font-mono text-xs" data-testid={`countdown-${domain}`}>
        —
      </span>
    );
  const c = formatCountdown(target);
  return (
    <span
      data-testid={`countdown-${domain}`}
      data-imminent={c.imminent ? "true" : "false"}
      className={`font-mono text-sm tabular-nums ${c.imminent ? "text-[#B45309] countdown-imminent px-1" : "text-[#101828]"}`}
    >
      {c.text}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.scheduled;
  return (
    <span
      className="inline-block font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-sm"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`status-${status}`}
    >
      {s.label}
    </span>
  );
}

function driftMinutes(target_iso, predicted_iso) {
  if (!target_iso || !predicted_iso) return null;
  const t = new Date(target_iso).getTime();
  const p = new Date(predicted_iso).getTime();
  if (Number.isNaN(t) || Number.isNaN(p)) return null;
  return Math.round((t - p) / 60000);
}

function DriftBadge({ minutes }) {
  if (minutes == null) return null;
  const abs = Math.abs(minutes);
  if (abs <= 30) return null;   // within tolerance — no badge
  const hours = Math.floor(abs / 60);
  const rem = abs % 60;
  const label = hours > 0 ? `${hours}h${rem ? ` ${rem}m` : ""}` : `${rem}m`;
  const sign = minutes > 0 ? "after" : "before";
  return (
    <span
      data-testid="drift-badge"
      className="inline-flex items-center gap-1 ml-2 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E]"
      title={`Your scheduled time is ${label} ${sign} the WHOIS-predicted drop. Double-check the target time.`}
    >
      <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2} />
      {label} {sign} drop
    </span>
  );
}

function AlertBadge({ snipe }) {
  if (!snipe.drop_alert_sent_at) return null;
  return (
    <span
      data-testid={`alert-sent-${snipe.domain}`}
      title={`Drop alert emailed ${snipe.drop_alert_sent_at}`}
      className="inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-[#ECFDF5] text-[#047857]"
    >
      <BellRing className="w-2.5 h-2.5" strokeWidth={2} />
      Alerted
    </span>
  );
}

function PreflightBadge({ snipe, onCheck, onAutofix, checking }) {
  const pf = snipe.preflight;
  const status = snipe.status;
  // Only meaningful for scheduled snipes — after fire, the actual result is on the row.
  if (status !== "scheduled") return null;

  if (!pf) {
    return (
      <button
        type="button"
        onClick={onCheck}
        disabled={checking}
        data-testid={`preflight-${snipe.domain}`}
        className="inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-[#F4F4F5] text-[#525252] hover:bg-[#03142f] hover:text-white transition"
        title="Run a validation ping against the registrar to catch config errors early"
      >
        <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2} />
        {checking ? "…" : "Pre-flight"}
      </button>
    );
  }

  const tooltip = (pf.checks || [])
    .map((c) => `${c.ok ? "✓" : "✗"} ${c.name}: ${c.message}${c.hint ? " — " + c.hint : ""}`)
    .join("\n") || pf.summary || "";

  const hasAutofix = (pf.checks || []).some((c) => !c.ok && c.auto_fix);

  if (pf.ok) {
    return (
      <span
        data-testid={`preflight-ok-${snipe.domain}`}
        className="inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-[#D1FAE5] text-[#065F46]"
        title={tooltip}
      >
        <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2} />
        Pre-flight OK
      </span>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      <button
        type="button"
        onClick={onCheck}
        disabled={checking}
        data-testid={`preflight-fail-${snipe.domain}`}
        className="inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-[#FEE2E2] text-[#991B1B] hover:bg-[#991B1B] hover:text-white transition"
        title={tooltip}
      >
        <ShieldAlert className="w-2.5 h-2.5" strokeWidth={2} />
        {checking ? "…" : (pf.summary || "Pre-flight failed")}
      </button>
      {hasAutofix && (
        <button
          type="button"
          onClick={onAutofix}
          disabled={checking}
          data-testid={`autofix-${snipe.domain}`}
          className="inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E] hover:bg-[#92400E] hover:text-white transition"
          title="Apply the auto-fix patches and re-check"
        >
          <Wand2 className="w-2.5 h-2.5" strokeWidth={2} />
          Fix now
        </button>
      )}
    </div>
  );
}

export default function WatchlistTable({ snipes, onChange }) {
  const { userTz } = useTimezone();
  const [checkingId, setCheckingId] = useState(null);
  const handleFire = async (id) => {
    try {
      await fireSnipe(id);
      toast.info("Firing now…");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fire failed");
    }
  };
  const handleCancel = async (id) => {
    try {
      await cancelSnipe(id);
      toast.warning("Snipe cancelled");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };
  const handleDelete = async (id) => {
    try {
      await deleteSnipe(id);
      toast.warning("Snipe removed");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const handleRetry = async (s) => {
    try {
      await retrySnipe(s.id);
      toast.success(`Re-armed ${s.domain} — firing in 5s`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  /** Patience mode — repeated retries every N min until 24h passes.
   *  Prompts the broker for interval + duration, then flips the snipe
   *  into patience mode via the new backend endpoint. Passing 0 cancels
   *  patience without changing the schedule. */
  const handlePatience = async (s) => {
    const currentMin = Number(s.patience_interval_min || 0);
    const answer = window.prompt(
      `Retry every N minutes if it fails (30 or 60). Enter 0 to cancel patience.\nCurrent: ${currentMin || "off"}`,
      currentMin ? String(currentMin) : "60",
    );
    if (answer === null) return;
    const interval = parseInt(answer, 10);
    if (isNaN(interval) || (interval !== 0 && interval !== 30 && interval !== 60)) {
      toast.error("Interval must be 30 or 60 (or 0 to cancel)");
      return;
    }
    try {
      await api.post(`/snipes/${s.id}/patience`, { interval_min: interval, duration_hours: 24 });
      toast.success(interval ? `Patience ON for ${s.domain} — every ${interval}m for 24h` : `Patience cancelled for ${s.domain}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Patience update failed");
    }
  };
  const handlePreflight = async (s) => {
    setCheckingId(s.id);
    try {
      const r = await preflightSnipe(s.id);
      if (r.ok) toast.success(`Pre-flight OK for ${s.domain}`);
      else toast.error(`Pre-flight failed: ${r.summary}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pre-flight check failed");
    } finally {
      setCheckingId(null);
    }
  };
  const handleAutofix = async (s) => {
    setCheckingId(s.id);
    try {
      const r = await autofixPreflight(s.id);
      const labels = (r.patches_applied || []).map((p) => p.label).join(", ");
      if (r.preflight?.ok) toast.success(`Fixed ${labels || "issues"} — pre-flight now OK`);
      else toast.warning(`Fixed ${labels}; other issues remain — see badge.`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed");
    } finally {
      setCheckingId(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="watchlist">
      <div className="px-6 py-3 flex items-center justify-between border-b border-[#eaecf0]">
        <div>
          <div className="overline">Watchlist</div>
          <div className="font-display text-lg font-medium mt-0.5">
            Scheduled Snipes
          </div>
        </div>
        <div className="text-xs font-mono text-[#A1A1AA] flex items-center gap-3">
          <span data-testid="watchlist-tz">TZ · {userTz}</span>
          <span>{snipes.length} entries</span>
        </div>
      </div>

      <div className="overflow-auto flex-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
              <th className="text-left px-6 py-2.5 overline">Domain</th>
              <th className="text-left px-4 py-2.5 overline">Target · {userTz}</th>
              <th className="text-left px-4 py-2.5 overline">Countdown</th>
              <th className="text-left px-4 py-2.5 overline">Mode</th>
              <th className="text-left px-4 py-2.5 overline">Registrar</th>
              <th className="text-left px-4 py-2.5 overline">Status</th>
              <th className="text-right px-6 py-2.5 overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {snipes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="overline mb-2">No snipes scheduled</div>
                  <div className="text-sm text-[#525252]">
                    Add a domain in the right panel to prime the sniper engine.
                  </div>
                </td>
              </tr>
            )}
            {snipes.map((s) => (
              <tr
                key={s.id}
                className="border-b border-[#eaecf0] hover:bg-[#FAFAFA] transition-colors"
                data-testid={`snipe-row-${s.domain}`}
              >
                <td className="px-6 py-3 font-mono text-[#101828]">{s.domain}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#525252]" title={`UTC: ${s.target_time}${s.predicted_drop_at ? ` · Predicted: ${s.predicted_drop_at}` : ""}`}>
                  {formatDateTime(s.target_time, userTz)}
                  <DriftBadge minutes={driftMinutes(s.target_time, s.predicted_drop_at)} />
                </td>
                <td className="px-4 py-3">
                  <CountdownCell target={s.target_time} status={s.status} domain={s.domain} />
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-sm bg-[#F4F4F5] text-[#525252]">
                    {s.retry_mode === "aggressive" ? `RETRY ${s.retry_window_seconds}s` : "SINGLE"}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#101828]">
                  {REGISTRARS[s.registrar]?.short || s.registrar}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={s.status} />
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <PreflightBadge
                      snipe={s}
                      onCheck={() => handlePreflight(s)}
                      onAutofix={() => handleAutofix(s)}
                      checking={checkingId === s.id}
                    />
                    <AlertBadge snipe={s} />
                  </div>
                  {s.result_message && (
                    <div className="text-[10px] text-[#A1A1AA] font-mono mt-1 max-w-[220px] truncate">
                      {s.result_message}
                    </div>
                  )}
                </td>
                <td className="px-6 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`row-menu-${s.domain}`}
                        className="p-1 hover:bg-[#F4F4F5] rounded-sm"
                      >
                        <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      {s.status === "scheduled" && (
                        <>
                          <DropdownMenuItem
                            onClick={() => handlePreflight(s)}
                            data-testid={`preflight-action-${s.domain}`}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                            Run pre-flight check
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleFire(s.id)}
                            data-testid={`fire-${s.domain}`}
                          >
                            <Play className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                            Fire now
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCancel(s.id)}
                            data-testid={`cancel-${s.domain}`}
                          >
                            <X className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                            Cancel
                          </DropdownMenuItem>
                        </>
                      )}
                      {(s.status === "failed" || s.status === "cancelled") && (
                        <DropdownMenuItem
                          onClick={() => handleRetry(s)}
                          data-testid={`retry-${s.domain}`}
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Try again now
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => handlePatience(s)}
                        data-testid={`patience-${s.domain}`}
                      >
                        <Timer className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                        {s.patience_interval_min
                          ? `Patience: every ${s.patience_interval_min}m · edit`
                          : "Retry every 30/60m for 24h"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(s.id)}
                        className="text-[#BE123C]"
                        data-testid={`delete-${s.domain}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
